import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarCheck, CalendarX, ScanLine } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  PortalError,
  getExternalSlip,
  getPortalSession,
  importExternalSlip,
  listExternalSlips,
  type ExternalSlip,
  type ImportSlipResult,
} from "./portalApi";

/**
 * Phiếu khám của phần mềm ngoài (MeetDoctor) — nơi mã QR của phiếu dẫn tới khi được in lại
 * thành địa chỉ cổng bệnh nhân: {@code /portal/slip?u=<địa chỉ phiếu>}.
 *
 * <p>Có {@code ?u=} thì đưa phiếu vào HIS ngay khi mở (một lần cho mỗi lần tải trang). HIS xử lý
 * như salepro: hồ sơ theo số điện thoại trên phiếu, dịch vụ vào danh mục, lượt khám hoàn thành,
 * lịch tái khám nếu phiếu ghi "Ngày tái khám" còn ở phía trước. Có {@code ?id=} thì mở lại một
 * phiếu đã lưu. Không có gì thì là ô dán địa chỉ + danh sách phiếu đã lưu.
 *
 * <p>Chưa đăng nhập → về trang đăng nhập kèm {@code next} để quay lại đúng đây.
 *
 * <p>OTP là của CỔNG (trình duyệt, không ai biết người cầm máy là ai). Cùng mã QR đó quét trong Zalo
 * Mini App thì KHÔNG qua đây: app gửi nguyên văn lên {@code POST /patients/me/external-slips} với JWT
 * bệnh nhân đã khớp số điện thoại Zalo, và HIS tự bóc {@code u} khỏi địa chỉ cổng
 * ({@code ExternalSlipImportService.unwrapPortalLink}) — không có lần xác thực thứ hai.
 */
export function PortalSlipPage() {
  const session = getPortalSession();
  const location = useLocation();
  const [search] = useSearchParams();
  const u = search.get("u");
  const id = search.get("id");

  if (!session) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/portal?next=${next}`} replace />;
  }

  return (
    <div className="min-h-screen bg-muted/30 px-4 py-6 font-sans text-foreground sm:py-8">
      <div className="mx-auto max-w-3xl space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-base font-bold text-primary">
              {session.clinicName || "Phòng khám"}
            </div>
            <div className="text-sm text-muted-foreground">
              Phiếu khám ngoài của{" "}
              <strong className="text-foreground">{session.patientName || "bạn"}</strong>
            </div>
          </div>
          <Button asChild type="button" variant="outline" size="sm">
            <Link to="/portal/me">Về trang hồ sơ</Link>
          </Button>
        </div>
        {u ? <ImportFromQr url={u} /> : id ? <SlipById id={id} /> : <ExternalSlipsTab />}
      </div>
    </div>
  );
}

/** Tab "Phiếu khám ngoài" trên trang hồ sơ: ô dán địa chỉ + danh sách phiếu đã lưu. */
export function ExternalSlipsTab() {
  const [url, setUrl] = useState("");
  const [result, setResult] = useState<ImportSlipResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const list = useQuery<ExternalSlip[]>({
    queryKey: ["portal", "me", "external-slips"],
    queryFn: listExternalSlips,
  });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await importExternalSlip(url.trim()));
      setUrl("");
      void qc.invalidateQueries({ queryKey: ["portal", "me"] });
    } catch (err) {
      setError(err instanceof PortalError ? err.message : "Không đưa được phiếu vào hồ sơ.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ScanLine className="h-4 w-4" aria-hidden="true" />
            Thêm phiếu khám từ mã QR
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="slip-url">Địa chỉ in trong mã QR trên phiếu</Label>
              <Input
                id="slip-url"
                inputMode="url"
                autoComplete="off"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://…meetdoctor.net/view-order/…"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Quét mã bằng máy ảnh điện thoại rồi dán địa chỉ vào đây. Phiếu được lưu cho đúng
              người mang số điện thoại in trên phiếu.
            </p>
            {error && <ErrorBox msg={error} />}
            <Button type="submit" disabled={busy || !url.trim()}>
              {busy ? "Đang đưa vào hồ sơ…" : "Đưa phiếu vào hồ sơ"}
            </Button>
          </form>
        </CardContent>
      </Card>
      {result && <SlipResult result={result} />}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Phiếu đã lưu</CardTitle>
        </CardHeader>
        <CardContent>
          {list.isLoading ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Đang tải…</p>
          ) : list.error ? (
            <ErrorBox
              msg={list.error instanceof Error ? list.error.message : "Không tải được."}
            />
          ) : !list.data?.length ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Chưa có phiếu khám ngoài nào.
            </p>
          ) : (
            <ul className="divide-y">
              {list.data.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{s.diagnosis || "Phiếu khám"}</div>
                    <div className="text-sm text-muted-foreground">
                      Khám {fmtDate(s.examDate)}
                      {s.nextVisitDate ? ` · Tái khám ${fmtDate(s.nextVisitDate)}` : ""}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {s.nextVisitDate &&
                      (s.followUpAppointmentId ? (
                        <Badge variant="success">Đã đặt lịch</Badge>
                      ) : (
                        <Badge variant="warning">Chưa đặt lịch</Badge>
                      ))}
                    <Button asChild variant="outline" size="sm">
                      <Link to={`/portal/slip?id=${encodeURIComponent(s.id)}`}>Xem</Link>
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** Mở từ mã QR: đưa phiếu vào HIS đúng một lần cho mỗi lần tải trang. */
function ImportFromQr({ url }: { url: string }) {
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "ok"; result: ImportSlipResult }
    | { kind: "error"; msg: string }
  >({ kind: "loading" });
  const started = useRef<string | null>(null);

  useEffect(() => {
    if (started.current === url) return;
    started.current = url;
    let alive = true;
    setState({ kind: "loading" });
    importExternalSlip(url)
      .then((result) => {
        if (alive) setState({ kind: "ok", result });
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setState({
          kind: "error",
          msg: err instanceof PortalError ? err.message : "Không đưa được phiếu vào hồ sơ.",
        });
      });
    return () => {
      alive = false;
    };
  }, [url]);

  if (state.kind === "loading") {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Đang đọc phiếu khám và ghi nhận lượt khám…
        </CardContent>
      </Card>
    );
  }
  if (state.kind === "error") {
    return (
      <Card>
        <CardContent className="space-y-3 py-6">
          <ErrorBox msg={state.msg} />
          <Button asChild variant="outline" size="sm">
            <Link to="/portal/slip">Nhập địa chỉ bằng tay</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }
  return <SlipResult result={state.result} />;
}

function SlipById({ id }: { id: string }) {
  const q = useQuery<ExternalSlip>({
    queryKey: ["portal", "me", "external-slips", id],
    queryFn: () => getExternalSlip(id),
  });
  if (q.isLoading)
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Đang tải…
        </CardContent>
      </Card>
    );
  if (q.error || !q.data)
    return <ErrorBox msg={q.error instanceof Error ? q.error.message : "Không tải được."} />;
  return <SlipCard slip={q.data} />;
}

function SlipResult({ result }: { result: ImportSlipResult }) {
  const { slip, alreadyImported } = result;
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
        {alreadyImported
          ? "Phiếu khám này đã có trong hồ sơ của bạn."
          : slip.visitAppointmentId
            ? "Đã lưu phiếu khám và ghi nhận lượt khám vào lịch sử của bạn."
            : "Đã lưu phiếu khám vào hồ sơ của bạn."}
      </div>
      <SlipCard slip={slip} />
    </div>
  );
}

function SlipCard({ slip }: { slip: ExternalSlip }) {
  const booked = Boolean(slip.followUpAppointmentId);
  return (
    <div className="space-y-4">
      {slip.nextVisitDate && (
        <div
          className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${
            booked
              ? "border-emerald-300 bg-emerald-50 text-emerald-900"
              : "border-amber-300 bg-amber-50 text-amber-900"
          }`}
          data-testid="follow-up-status"
        >
          {booked ? (
            <CalendarCheck className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          ) : (
            <CalendarX className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          )}
          <div>
            <div className="font-semibold">Tái khám ngày {fmtDate(slip.nextVisitDate)}</div>
            <div className="text-sm">
              {booked ? (
                <>
                  Phòng khám đã đặt sẵn lịch tái khám cho bạn vào ngày này.{" "}
                  <Link to="/portal/me" className="font-medium underline underline-offset-4">
                    Xem trong Lịch hẹn
                  </Link>
                  .
                </>
              ) : (
                "Chưa đặt được lịch tái khám cho ngày này. Vui lòng liên hệ phòng khám để được xếp lịch."
              )}
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Phiếu khám · {sourceLabel(slip.source)}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
            <Row k="Bệnh nhân" v={slip.patientName || "—"} />
            <Row k="Ngày khám" v={fmtDate(slip.examDate)} />
            <Row k="Chẩn đoán" v={slip.diagnosis || "—"} />
            <Row
              k="Lượt khám"
              v={slip.visitAppointmentId ? "Đã ghi nhận (xem tab Lịch hẹn)" : "Chưa ghi nhận"}
            />
            <Row k="Đưa vào hồ sơ" v={fmtDate(slip.importedAt)} />
          </dl>

          {slip.services.length > 0 && (
            <section>
              <h3 className="mb-2 font-semibold">Dịch vụ đã thực hiện</h3>
              <table className="w-full [font-variant-numeric:tabular-nums]">
                <tbody>
                  {slip.services.map((line, i) => (
                    <tr key={`${line.name}-${i}`} className="border-b last:border-0">
                      <td className="py-1.5 pr-2">
                        {line.name}
                        {line.quantity > 1 ? ` ×${line.quantity}` : ""}
                      </td>
                      <td className="py-1.5 text-right">{vnd(line.amount)}</td>
                    </tr>
                  ))}
                  {slip.discountAmount > 0 && (
                    <tr className="text-muted-foreground">
                      <td className="py-1.5 pr-2">Giảm giá</td>
                      <td className="py-1.5 text-right">-{vnd(slip.discountAmount)}</td>
                    </tr>
                  )}
                  <tr className="font-semibold">
                    <td className="pt-2 pr-2">Tổng cộng</td>
                    <td className="pt-2 text-right">{vnd(slip.totalAmount)}</td>
                  </tr>
                </tbody>
              </table>
              <p className="mt-1 text-xs text-muted-foreground">
                Số liệu in trên phiếu của phần mềm phòng khám, để đối chiếu.
              </p>
            </section>
          )}

          <HtmlBlock title="Toa thuốc" html={slip.prescriptionHtml} />
          <HtmlBlock title="Kết quả xét nghiệm" html={slip.labResultHtml} />
        </CardContent>
      </Card>
    </div>
  );
}

function HtmlBlock({ title, html }: { title: string; html: string | null }) {
  if (!html) return null;
  return (
    <section>
      <h3 className="mb-2 font-semibold">{title}</h3>
      {/* HIS đã làm sạch HTML này bằng Jsoup.clean trước khi lưu. */}
      <div
        className="overflow-x-auto [&_table]:w-full [&_td]:border [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:px-2 [&_th]:py-1"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </section>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-32 shrink-0 text-muted-foreground">{k}</dt>
      <dd className="min-w-0 font-medium">{v}</dd>
    </div>
  );
}

function ErrorBox({ msg }: { msg: string }) {
  return (
    <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
      {msg}
    </div>
  );
}

function sourceLabel(source: string): string {
  return source === "MEETDOCTOR" ? "MeetDoctor" : source;
}

function fmtDate(v: string | null | undefined): string {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString("vi-VN");
}

function vnd(n: number): string {
  return `${Math.round(n).toLocaleString("vi-VN")}đ`;
}
